import React from 'react';
import {
	actionRequiredService,
	GET_ACTION_REQUIRED,
	GET_CASES_FILES,
	GET_FOLLOW_UP_QUESTIONS,
} from '@/api';
import { SurveyQuestionsModal } from '@/components/modals/survey/SurveyQuestionsModal';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CaseQuestionI, SendFollowUpPayload } from '@/interfaces';
import { useNotification } from '@/hooks';
import { formatErrorMessage } from '@/lib';

interface Props {
	isOpen: boolean;
	actionRequiredId: number | undefined;
	setIsModalOpen: (value: boolean) => void;
}

export const ModalFollowUpQuestions: React.FC<Props> = ({
	isOpen,
	actionRequiredId,
	setIsModalOpen,
}) => {
	const { toastSuccess, toastError } = useNotification();
	const queryClient = useQueryClient();
	const { data: action, isLoading } = useQuery({
		queryKey: [GET_FOLLOW_UP_QUESTIONS, actionRequiredId],
		queryFn: () =>
			actionRequiredService.getFollowUp(actionRequiredId as number),
	});

	const handleSuccess = () => {
		setIsModalOpen(false);
		queryClient.invalidateQueries({ queryKey: [GET_ACTION_REQUIRED] });
		queryClient.invalidateQueries({ queryKey: [GET_CASES_FILES] });
		toastSuccess('Survey submitted successfully');
	};

	const { mutateAsync: sendFollowUp } = useMutation({
		mutationFn: (data: SendFollowUpPayload) =>
			actionRequiredService.sendFollowUp(actionRequiredId as number, data),
		onSuccess: () => handleSuccess(),
		onError: () => toastError(formatErrorMessage(undefined)),
	});

	const options: CaseQuestionI[] = React.useMemo(() => {
		if (!action?.data) return [];

		return action.data.questions.map((q) => ({
			id: q.id,
			question: q.text,
			question_type: q.type,
			multiple_choise_questions: [],
			ask_explanation: q.explanation,
			incorrect_answer: q.options
				?.find((o) => !o.is_correct)
				?.text?.toUpperCase(),
		}));
	}, [action]);

	if (isLoading) {
		return null;
	}

	return (
		<SurveyQuestionsModal
			heading={action?.data.heading}
			description={action?.data.description}
			title={action?.data?.name ?? 'Answer Follow-Up'}
			questions={options}
			isOpen={isOpen}
			onClose={() => setIsModalOpen(false)}
			onSubmit={async (data) => {
				await sendFollowUp({ ...data, decline: false });
			}}
		/>
	);
};
